/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED, } from "../../../../share/util/api/polling";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import error from '../../../../common/nodejs/errors';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';

/** DID定義 */
const DID_SUCCESS = "10A1";
const DID_HORIZONTAL = "10A4";
const DID_VERTICAL = "10A5";

/** 各フェーズ定義 */
const PHASE = {
  CONFIRM_START: 'confirmStart', // 開始確認
  CONFIRM_BUMPER_REPLACEMENT: "confirmBumperReplacement", // バンパー交換確認
  CONFIRM_PRE_PAINTED_BUMPER: "confirmPrePaintedBumper",  // 純正塗装済みバンパー交換確認
  EXECUTE: "execute", // 実行監視
  SUCCESS: "success", // 正常範囲表示
  SUCCESS_CONFIRM: "successConfirm", // レーダ反射強度確認表示
  CONFIRM_THRESHOLD: "confirmThreshold", // レーダ反射強度閾値表示
  FAILED_EXECUTE: "failedExecute", // 点検失敗表示
  FAILED_ENVIRONMENT: "failedEnvironment", // 点検環境不備表示
  ERROR: "error", //共通エラー表示
};

export default {
  name: "requirement169",
  data() {
    return {
      phase: PHASE.CONFIRM_START,
      isDispose: true,
      isLoading: false,
      systemId: "",
      modal_requirement_169: false,
      exitPromise: null,
      isReflectIntensity: false,
      successDeg: null,
      horizontalDeg: null,
      verticalDeg: null,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED_EXECUTE:
        case PHASE.FAILED_ENVIRONMENT:
        case PHASE.ERROR:
          return "danger";
        case PHASE.SUCCESS:
          return "success";
        case PHASE.CONFIRM_START:
        case PHASE.CONFIRM_BUMPER_REPLACEMENT:
        case PHASE.CONFIRM_PRE_PAINTED_BUMPER:
        case PHASE.EXECUTE:
        case PHASE.SUCCESS_CONFIRM:
        case PHASE.CONFIRM_THRESHOLD:
        default:
          return "secondary";
      }
    },

    headerTitle() {
      // 実行中
      const executionTitle = 'individual.common_title_execution';
      // 確認
      const confirmationTitle = 'message.common_title_confirmation';
      // 成功
      const successTitle = 'message.common_title_success';
      // エラー
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.CONFIRM_START]: confirmationTitle,
        [PHASE.CONFIRM_BUMPER_REPLACEMENT]: confirmationTitle,
        [PHASE.CONFIRM_PRE_PAINTED_BUMPER]: confirmationTitle,
        [PHASE.EXECUTE]: executionTitle,
        [PHASE.SUCCESS]: successTitle,
        [PHASE.SUCCESS_CONFIRM]: confirmationTitle,
        [PHASE.CONFIRM_THRESHOLD]: confirmationTitle,
        [PHASE.FAILED_EXECUTE]: errorTitle,
        [PHASE.FAILED_ENVIRONMENT]: errorTitle,
        [PHASE.ERROR]: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        [PHASE.CONFIRM_START]: `${this.i18nWsKey}.REQ_WS_169_TXT_1`,
        [PHASE.CONFIRM_BUMPER_REPLACEMENT]: `${this.i18nWsKey}.REQ_WS_169_TXT_2`,
        [PHASE.CONFIRM_PRE_PAINTED_BUMPER]: `${this.i18nWsKey}.REQ_WS_169_TXT_3`,
        [PHASE.EXECUTE]: `${this.i18nWsKey}.REQ_WS_169_TXT_4`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_169_TXT_5`,
        [PHASE.SUCCESS_CONFIRM]: `${this.i18nWsKey}.REQ_WS_169_TXT_6`,
        [PHASE.CONFIRM_THRESHOLD]: `${this.i18nWsKey}.REQ_WS_169_TXT_7`,
        [PHASE.FAILED_EXECUTE]: `${this.i18nWsKey}.REQ_WS_169_TXT_8_1201`,
        [PHASE.FAILED_ENVIRONMENT]: `${this.i18nWsKey}.REQ_WS_169_TXT_9_1202`,
        [PHASE.ERROR]: 'individual.error_communication_error',
      };
      switch (this.phase) {
        case PHASE.CONFIRM_THRESHOLD:
          return this.$t(messageMap[this.phase]).replace('$DID$', this.successDeg);
        case PHASE.FAILED_EXECUTE:
          return this.$t(messageMap[this.phase]).replace('$DID$', this.horizontalDeg).replace('$DID$', this.verticalDeg);
      }
      return this.$t(messageMap[this.phase]);
    },

    showButtonYesNo() {
      const showYesNoButtonMap = {
        [PHASE.CONFIRM_START]: true,
        [PHASE.CONFIRM_BUMPER_REPLACEMENT]: true,
        [PHASE.CONFIRM_PRE_PAINTED_BUMPER]: true,
        [PHASE.EXECUTE]: false,
        [PHASE.SUCCESS]: false,
        [PHASE.SUCCESS_CONFIRM]: false,
        [PHASE.CONFIRM_THRESHOLD]: false,
        [PHASE.FAILED_EXECUTE]: false,
        [PHASE.FAILED_ENVIRONMENT]: false,
        [PHASE.ERROR]: false,
      };
      return showYesNoButtonMap[this.phase] ?? false;
    },

    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.CONFIRM_START]: false,
        [PHASE.CONFIRM_BUMPER_REPLACEMENT]: false,
        [PHASE.CONFIRM_PRE_PAINTED_BUMPER]: false,
        [PHASE.EXECUTE]: false,
        [PHASE.SUCCESS]: true,
        [PHASE.SUCCESS_CONFIRM]: true,
        [PHASE.CONFIRM_THRESHOLD]: true,
        [PHASE.FAILED_EXECUTE]: true,
        [PHASE.FAILED_ENVIRONMENT]: true,
        [PHASE.ERROR]: true,
      };
      return showOkButtonMap[this.phase] ?? false;
    },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    async init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_169 = true;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_169 = false;
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      await this.exitProcess();
      // 共通エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {object} calledFrom 呼び出し元
     */
    validateErrorResult(result, calledFrom = '') {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          this.showUnexpectedError();
        },
      );
    },

    /**
     * ルーチン終了処理。isDispose フラグに応じて dispose または terminate API を呼び出す。
	   */
    async exitProcess() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitPromise = this.isDispose ? this.dispose() : this.terminate();
      await this.exitPromise;
    },

    /** NOボタン押下時の処理 */
    async onNoClicked() {
      switch (this.phase) {
        case PHASE.CONFIRM_START:
          this.closeWizard();
          return;
        case PHASE.CONFIRM_BUMPER_REPLACEMENT:
          this.isReflectIntensity = false;
          break;
        case PHASE.CONFIRM_PRE_PAINTED_BUMPER:
          this.isReflectIntensity = true;
          break;
        default:
          return;
      }
      await this.execute();
    },

      /** YESボタン押下時の処理 */
    async onYesClicked() {
      switch (this.phase) {
        case PHASE.CONFIRM_START:
          this.phase = PHASE.CONFIRM_BUMPER_REPLACEMENT;
          break;
        case PHASE.CONFIRM_BUMPER_REPLACEMENT:
          this.phase = PHASE.CONFIRM_PRE_PAINTED_BUMPER;
          break;
        case PHASE.CONFIRM_PRE_PAINTED_BUMPER:
          this.isReflectIntensity = false;
          await this.execute();
          break;
        default:
          break;
      }
    },

    /** OKボタン押下時の処理 */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.SUCCESS:
        case PHASE.CONFIRM_THRESHOLD:
        case PHASE.FAILED_EXECUTE:
        case PHASE.FAILED_ENVIRONMENT:
        case PHASE.ERROR:
          this.closeWizard();
          break;
        case PHASE.SUCCESS_CONFIRM:
          await this.terminate();
          this.phase = PHASE.CONFIRM_THRESHOLD;
          break;
        default:
          break;
      }
    },

    /** ルーチン実行 */
    async execute() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        this.isDispose = response.data.user_data.is_dispose;
        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          this.phase = PHASE.EXECUTE; // 正常終了：実行監視に遷移
          await this.monitor(); // ルーチン実行監視を実行
        } else {
          await this.showUnexpectedError(); // 異常終了：共通エラーに遷移
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** ルーチン結果確認 */
    async monitor() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'monitor'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            await this.readInspectionSuccess(); // データ読出し(点検成功表示)を実行
            break;
          // 異常終了
          case REQUEST_STATUS_FAILED:
            switch (response.data.async_code) {
              case error.WSP_169_INSPECTION_FAILED:
                await this.readInspectionFailed(); // データ読出し(点検失敗表示)を実行
                break;
              case error.WSP_169_INSPECTION_ENVIRONMENT_DEFICIENCIES:
                await this.terminate();
                this.phase = PHASE.FAILED_ENVIRONMENT; // 点検環境不備表示へ遷移
                break;
              default:
                await this.showUnexpectedError(); // 共通エラーへ遷移
                break;
            }
            break;
          default:
            await this.showUnexpectedError(); // 共通エラーに遷移
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * データ読出し(点検成功表示)
     */
    async readInspectionSuccess() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read-inspectionSuccess'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        if (response.data.status !== REQUEST_STATUS_COMPLETED) {
          await this.showUnexpectedError(); // 異常終了：共通エラーに遷移
          return;
        }

        if (this.isReflectIntensity) {
          // 反射強度確認要否：必要の場合、レーダ反射強度確認表示へ遷移
          const userData = response.data.user_data;
          //DIDのByte1要素を取得
          const successData = { readout_data: (userData?.readout_data ?? []).filter(item => item.did?.startsWith(DID_SUCCESS)) };
          // 表示用DID編集処理
          const readOutData = await individualWorkSupportUtil.getReadOutData(this, this.systemId, successData);
          // DID編集後のデータから、値と単位のみ取得
          const successParts = readOutData ? readOutData.split(':') : [];
          this.successDeg = (successParts.length > 1 ? successParts[1] : '0').match(/^-?[\d.]+/)?.[0] ?? '0';

          this.phase = PHASE.SUCCESS_CONFIRM;
        } else {
          // 反射強度確認要否：不要の場合、正常範囲表示へ遷移
          await this.terminate(); // 終了処理を実行
          this.phase = PHASE.SUCCESS;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * データ読出し(点検失敗表示)
     */
    async readInspectionFailed() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read-inspectionFailed'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        if (response.data.status !== REQUEST_STATUS_COMPLETED) {
          await this.showUnexpectedError(); // 異常終了：共通エラーに遷移
          return;
        }
        await this.terminate();

        const userData = response.data.user_data;

        // DIDのByte1要素を取得
        const horizontalUserData = { readout_data: (userData?.readout_data ?? []).filter(item => item.did?.startsWith(DID_HORIZONTAL)) };
        const verticalUserData = { readout_data: (userData?.readout_data ?? []).filter(item => item.did?.startsWith(DID_VERTICAL)) };

        // 表示用DID編集処理
        const horizontalReadOutData = await individualWorkSupportUtil.getReadOutData(this, this.systemId, horizontalUserData);
        const verticalReadOutData = await individualWorkSupportUtil.getReadOutData(this, this.systemId, verticalUserData);

        // DID編集後のデータから値取得（正規表現で先頭の数値部分を抽出）
        const horizontalParts = horizontalReadOutData ? horizontalReadOutData.split(':') : [];
        this.horizontalDeg = (horizontalParts.length > 1 ? horizontalParts[1] : '0').match(/^-?[\d.]+/)?.[0] ?? '0';
        const verticalParts = verticalReadOutData ? verticalReadOutData.split(':') : [];
        this.verticalDeg = (verticalParts.length > 1 ? verticalParts[1] : '0').match(/^-?[\d.]+/)?.[0] ?? '0';

        this.phase = PHASE.FAILED_EXECUTE;
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了 */
    async terminate() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'terminate'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'terminate'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'terminate'); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /** dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'dispose'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'dispose'); },
        );
      } finally {
        this.isLoading = false;
      }
    },
  },

  mounted() {
    this.init();
  }
};
